import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import type { SxProps } from "@mui/material";
import type { ReactNode } from "react";

interface AdvancedOptionCardProps {
  label: string;
  labelTooltip?: string;
  onClose?: VoidFunction;
  children: ReactNode;
  color?: "primary" | "default";
  showCloseButton?: boolean;
  sx?: SxProps;
}

export function AdvancedOptionCard({
  label,
  labelTooltip,
  onClose,
  children,
  color = "primary",
  showCloseButton = true,
  sx,
}: AdvancedOptionCardProps) {
  const isPrimary = color === "primary";
  return (
    <Box
      sx={[
        {
          position: "relative",
          border: "1px solid",
          borderColor: isPrimary ? "primary.main" : "grey.400",
          borderRadius: 1,
          pl: 1.5,
          pr: showCloseButton ? 5 : 1.5,
          pb: 1,
          pt: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          position: "absolute",
          top: -10,
          left: 10,
          bgcolor: "background.paper",
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          color={isPrimary ? "primary" : "text.secondary"}
          sx={{ lineHeight: 1.5 }}
        >
          {label}
        </Typography>
        {labelTooltip && (
          <Tooltip title={labelTooltip}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: isPrimary ? "primary.main" : "text.secondary",
              }}
            />
          </Tooltip>
        )}
      </Stack>
      {showCloseButton && onClose && (
        <Tooltip title={`Close ${label}`}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={`Close ${label}`}
            sx={{
              position: "absolute",
              top: "50%",
              right: 4,
              transform: "translateY(-50%)",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {children}
    </Box>
  );
}
